import type { PDFDocumentLoadingTask, PDFDocumentProxy } from 'pdfjs-dist';
import { UPLOAD_MAX_BYTES } from '~/components/kiosk/kiosk-ten-dlc/registration/registrationImage';

const MAX_PASSES = 4;
const MAX_PAGES = 50;
// A short scan keeps its 300 dpi; the loop only steps down as far as the cap forces, and at 72 dpi a
// 10pt EIN is still readable, below it nothing is.
const START_SCALE = 300 / 72;
const MIN_SCALE = 1;
const JPEG_QUALITY = 0.7;

type RenderedPage = { jpeg: Uint8Array; width: number; height: number };

const toJpeg = (canvas: HTMLCanvasElement): Promise<Blob> =>
    new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => (blob ? resolve(blob) : reject(new Error('The page could not be encoded.'))),
            'image/jpeg',
            JPEG_QUALITY
        );
    });

const loadPdf = async (file: File): Promise<PDFDocumentLoadingTask> => {
    // Loaded on demand: pdf.js and its worker are around 1.5 MiB.
    const [pdfjs, { default: workerSrc }] = await Promise.all([
        import('pdfjs-dist'),
        import('pdfjs-dist/build/pdf.worker.min.mjs?worker&url'),
    ]);
    pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;

    return pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) });
};

const openPdf = async (task: PDFDocumentLoadingTask): Promise<PDFDocumentProxy> => {
    try {
        return await task.promise;
    } catch (e) {
        if (e instanceof Error && 'PasswordException' === e.name) {
            throw new Error('The PDF is password-protected; remove the password first.', { cause: e });
        }

        throw new Error('The file could not be read as a PDF.', { cause: e });
    }
};

// Stops as soon as the JPEGs alone overflow the cap, so a hopeless pass costs a page or two, not the whole file.
const renderPages = async (pdf: PDFDocumentProxy, scale: number): Promise<RenderedPage[]> => {
    const canvas = document.createElement('canvas');
    const pages: RenderedPage[] = [];
    let total = 0;

    for (let number = 1; number <= pdf.numPages && total <= UPLOAD_MAX_BYTES; number += 1) {
        const page = await pdf.getPage(number);
        const viewport = page.getViewport({ scale });
        canvas.width = Math.round(viewport.width);
        canvas.height = Math.round(viewport.height);
        await page.render({ canvas, viewport }).promise;
        page.cleanup();

        const jpeg = new Uint8Array(await (await toJpeg(canvas)).arrayBuffer());
        const { width, height } = page.getViewport({ scale: 1 });
        pages.push({ jpeg, width, height });
        total += jpeg.byteLength;
    }

    return pages;
};

const toPdf = async (pages: RenderedPage[]): Promise<Uint8Array<ArrayBuffer>> => {
    const { PDFDocument } = await import('@cantoo/pdf-lib');
    const document = await PDFDocument.create();

    for (const { jpeg, width, height } of pages) {
        const image = await document.embedJpg(jpeg);
        document.addPage([width, height]).drawImage(image, { x: 0, y: 0, width, height });
    }

    // Object streams only compress the few bytes around the JPEGs.
    return document.save({ useObjectStreams: false });
};

// A scan is a container for images, so every page is re-rendered as one JPEG and the
// resolution shrinks until the rebuilt file fits, as prepareImage does for a single image.
export const preparePdf = async (file: File): Promise<File> => {
    if (file.size <= UPLOAD_MAX_BYTES) {
        return file;
    }

    const task = await loadPdf(file);

    try {
        const pdf = await openPdf(task);

        if (pdf.numPages > MAX_PAGES) {
            throw new Error(`The PDF has ${pdf.numPages} pages; attach only the ones that matter.`);
        }

        let scale = START_SCALE;

        for (let pass = 0; pass < MAX_PASSES; pass += 1) {
            const pages = await renderPages(pdf, scale);
            let size = (pages.reduce((sum, page) => sum + page.jpeg.byteLength, 0) / pages.length) * pdf.numPages;

            if (pages.length === pdf.numPages) {
                const bytes = await toPdf(pages);

                if (bytes.byteLength <= UPLOAD_MAX_BYTES) {
                    return new File([bytes], file.name, { type: 'application/pdf' });
                }

                size = bytes.byteLength;
            }

            if (MIN_SCALE === scale) {
                break;
            }

            // JPEG size shrinks a little slower than the pixel count, so aim 10% under the cap,
            // never below half per pass and never under the floor.
            scale = Math.max(MIN_SCALE, scale * Math.max(0.5, Math.sqrt((0.9 * UPLOAD_MAX_BYTES) / size)));
        }

        throw new Error('The PDF is too large even after compressing; split it first.');
    } finally {
        await task.destroy();
    }
};
